// pages/HomePage.tsx - A Dinâmica: pedido, espera e pronta (blueprint §3.2; D25, D77, D78, D80, D83).
//
// A geração é a mesma de antes (F10): o mesmo pedido à API da Dinâmica, que salva a atividade sozinha.
// Muda a apresentação: uma coisa por vez, com o foco no título de cada estado, os erros junto do que falhou
// e as proteções da espera.

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, ConfirmDialog, Notice } from '@winieduca/ui';
import { gerarDinamica } from '../services/api';
import { deletarAtividade, listarAtividades } from '../services/historicoService';
import { registrarEvento, usoDoDia } from '../services/hubApi';
import { useAppContext } from '../contexts/AppContext';
import type { AtividadeHistorico, ResponseDinamica } from '../types/index';
import { erros as textosErros, espera as textosEspera, esperaNovos, limites, semInternet } from '../content/textos';
import { useSaidaProtegida, useSegundos, useTelaAcesa } from '../hooks/useEspera';
import { Pedido, validarPedido, type ErrosDoPedido, type ValoresDoPedido } from '../telas/Pedido';
import { Recebe } from '../telas/Recebe';
import { Espera } from '../telas/Espera';
import { Pronta } from '../telas/Pronta';
import { SuasDinamicas, type ListaDeDinamicas } from '../telas/SuasDinamicas';

type Estado = { tela: 'pedido' } | { tela: 'espera' } | { tela: 'pronta'; resultado: ResponseDinamica; atividadeId?: string };
type Aviso = { tom: 'warning' | 'error'; texto: string } | null;

const VALORES_INICIAIS: ValoresDoPedido = { faixaEtaria: 8, materia: '', objetivo: '', espaco: '', inclusao: [], refinamento: '' };

/** O que o professor vê quando a criação falha: o limite do dia (07_VOZ §5c) ou o erro, sem detalhe técnico. */
export function avisoDaFalha(erro: unknown): { aviso: NonNullable<Aviso>; motivo?: 'cota' | 'protecao' | 'global' } {
  if (axios.isAxiosError(erro)) {
    const status = erro.response?.status;
    const dados = erro.response?.data as { reason?: 'cota' | 'protecao' | 'global' } | undefined;
    if (status === 429 && dados?.reason) {
      return { aviso: { tom: 'warning', texto: dados.reason === 'cota' ? limites.cota : limites.pausa }, motivo: dados.reason };
    }
    if (!erro.response) return { aviso: { tom: 'error', texto: semInternet.texto } };
    if (status === 400) return { aviso: { tom: 'error', texto: textosErros.campos } };
  }
  return { aviso: { tom: 'error', texto: textosErros.criar } };
}

export default function HomePage() {
  const { opcoes, recarregarOpcoes } = useAppContext();
  const [estado, setEstado] = useState<Estado>({ tela: 'pedido' });
  const [valores, setValores] = useState<ValoresDoPedido>(VALORES_INICIAIS);
  const [erros, setErros] = useState<ErrosDoPedido>({});
  const [aviso, setAviso] = useState<Aviso>(null);
  const [lista, setLista] = useState<ListaDeDinamicas>({ estado: 'carregando' });
  const [confirmarSaida, setConfirmarSaida] = useState<(() => void) | null>(null);
  // Começa em 'pedido': ao chegar do hub, a página abre no topo sem mover o foco (só as trocas de tela movem).
  const tituloRef = useRef<string>('pedido');

  const emEspera = estado.tela === 'espera';
  const segundos = useSegundos(emEspera);
  useTelaAcesa(emEspera);
  useSaidaProtegida(emEspera, (sair) => setConfirmarSaida(() => sair));

  // A cada troca de estado, a tela volta ao topo e o foco vai para o título (leitor de tela e teclado).
  useEffect(() => {
    if (tituloRef.current === estado.tela) return;
    tituloRef.current = estado.tela;
    window.scrollTo({ top: 0 });
    document.querySelector<HTMLElement>('main h1')?.focus({ preventScroll: true });
  }, [estado.tela]);

  const carregarLista = useCallback(async () => {
    setLista({ estado: 'carregando' });
    try {
      setLista({ estado: 'pronta', itens: await listarAtividades() });
    } catch {
      setLista({ estado: 'falha' });
    }
  }, []);

  // Limite do dia: aviso a partir de 3 restantes; no limite, a mensagem da cota (D109).
  const conferirUso = useCallback(async () => {
    const uso = await usoDoDia();
    if (!uso) return;
    if (uso.restantes === 0) setAviso({ tom: 'warning', texto: limites.cota });
    else if (uso.restantes <= 3) setAviso({ tom: 'warning', texto: limites.restam(uso.restantes) });
  }, []);

  useEffect(() => {
    void carregarLista();
    void conferirUso();
  }, [carregarLista, conferirUso]);

  function mudar(parcial: Partial<ValoresDoPedido>) {
    setValores((v) => ({ ...v, ...parcial }));
    // O erro de um campo some quando ele é corrigido.
    setErros((e) => {
      const novo = { ...e };
      for (const chave of Object.keys(parcial) as (keyof ErrosDoPedido)[]) delete novo[chave];
      return novo;
    });
  }

  async function criar() {
    const novos = validarPedido(valores);
    setErros(novos);
    if (Object.keys(novos).length) {
      const primeiro = Object.keys(novos)[0];
      requestAnimationFrame(() => document.querySelector<HTMLElement>(`[name="${primeiro}"]`)?.focus());
      return;
    }
    setAviso(null);
    setEstado({ tela: 'espera' });
    try {
      const resultado = await gerarDinamica({
        faixaEtaria: valores.faixaEtaria,
        materia: valores.materia,
        objetivo: valores.objetivo,
        espaco: valores.espaco,
        inclusao: valores.inclusao,
        refinamento: valores.refinamento.trim() || undefined,
      });
      setEstado({ tela: 'pronta', resultado, atividadeId: resultado.geracaoId });
      void carregarLista();
    } catch (erro) {
      const { aviso: novo, motivo } = avisoDaFalha(erro);
      if (motivo) registrarEvento({ event: 'limite_atingido', props: { motivo } });
      setAviso(novo);
      setEstado({ tela: 'pedido' });
    }
  }

  function abrir(a: AtividadeHistorico) {
    setEstado({ tela: 'pronta', resultado: a.resultado, atividadeId: a.resultado.geracaoId || a.id });
  }

  async function remover(id: string) {
    const ok = await deletarAtividade(id);
    if (ok) setLista((l) => (l.estado === 'pronta' ? { estado: 'pronta', itens: l.itens.filter((a) => a.id !== id) } : l));
    return ok;
  }

  function criarOutra() {
    setEstado({ tela: 'pedido' });
    void conferirUso();
  }

  if (!opcoes) {
    return (
      <div className="din-pagina">
        <Notice tone="error" action={<Button onClick={recarregarOpcoes}>{textosErros.tentar}</Button>}>
          {textosErros.opcoes}
        </Notice>
      </div>
    );
  }

  // O texto aprovado tem a consequência e a pergunta; o diálogo põe a pergunta no título (DESIGN_SYSTEM §6).
  const corte = textosEspera.sair.indexOf('. ') + 1;
  const consequencia = textosEspera.sair.slice(0, corte);
  const pergunta = textosEspera.sair.slice(corte).trim();

  return (
    <div className="din-pagina" data-tela={estado.tela}>
      {estado.tela === 'pedido' && (
        <>
          <div className="din-pedido-grade">
            <Pedido
              opcoes={opcoes}
              valores={valores}
              mudar={mudar}
              erros={erros}
              enviando={false}
              aviso={aviso && <Notice tone={aviso.tom} live>{aviso.texto}</Notice>}
              onCriar={() => void criar()}
            />
            <Recebe />
            <SuasDinamicas lista={lista} onAbrir={abrir} onRemover={remover} />
          </div>
        </>
      )}
      {estado.tela === 'espera' && <Espera segundos={segundos} />}
      {estado.tela === 'pronta' && <Pronta resultado={estado.resultado} atividadeId={estado.atividadeId} onCriarOutra={criarOutra} />}

      {/* Sair durante a espera (D80): "Se sair agora, a dinâmica não fica pronta." + "Sair mesmo assim?". */}
      <ConfirmDialog
        open={confirmarSaida !== null}
        title={pergunta}
        confirmLabel={esperaNovos.confirmarSair}
        cancelLabel={esperaNovos.ficar}
        onConfirm={() => {
          const sair = confirmarSaida;
          setConfirmarSaida(null);
          sair?.();
        }}
        onCancel={() => setConfirmarSaida(null)}
      >
        {consequencia}
      </ConfirmDialog>
    </div>
  );
}
